import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import BackgroundImage from "../../assets/background.png";
import Logo1 from "../../utilities/dummy_assets/MBF_Logo.png";
import mapITlogo from "../../utilities/dummy_assets/svgs/MapIt_Logo.svg";
import PageTransition from "components/PageTransition/PageTransition";

const Capture = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  const [imageData, setImageData] = useState(null);
  const [stream, setStream] = useState(null);
  const [hasCameraPermission, setHasCameraPermission] = useState(null);
  const [loader, setLoader] = useState(true);

  // Start camera on mount and cleanup on unmount
  useEffect(() => {
    if (hasCameraPermission === null || hasCameraPermission) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [hasCameraPermission]);

  const startCamera = async () => {
    try {
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
        videoRef.current.srcObject = null;
      }

      const currentStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      if (videoRef.current) {
        const video = videoRef.current;
        video.srcObject = currentStream;

        // Wait until the metadata is loaded before playing
        video.onloadedmetadata = async () => {
          try {
            await video.play();
          } catch (error) {
            console.error("Error playing video:", error);
            toast.error(
              "Unable to start video playback. Please check your camera and browser settings.",
            );
          } finally {
            setLoader(false);
          }
        };
      }
      setStream(currentStream);
      setHasCameraPermission(true);

      // Listen for stream interruption (e.g., permission revoked)
      currentStream.getVideoTracks()[0].onended = () => {
        setHasCameraPermission(false);
        setStream(null);
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      };
    } catch (err) {
      console.error("Error accessing webcam:", err);
      setLoader(false);
      setHasCameraPermission(false);
      setStream(null);
      if (err.name !== "NotAllowedError") {
        toast.error(
          "An error occurred while accessing the camera. Please try again.",
        );
      }
    }
  };

  /**
   * Stop all video tracks to release camera
   */
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  /**
   * Capture current video frame to canvas and convert to data URL
   */
  const captureImage = () => {
    if (
      !hasCameraPermission ||
      !stream ||
      !videoRef.current ||
      !canvasRef.current
    )
      return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to base64 image data
    const dataUrl = canvas.toDataURL("image/png");
    setImageData(dataUrl);
    stopCamera();
  };

  /**
   * Discard captured image and restart camera
   */
  const handleRetake = async () => {
    setImageData(null);
    await startCamera(); // Restart camera
  };

  /**
   * Navigate to AdminOrgDetail with captured image
   */
  const handleUpload = () => {
    navigate("/student-detail-1", { state: { capturedImage: imageData } });
  };
  useEffect(() => {
    // Removed debug console.log before production deployment
  }, [loader]);
  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        boxSizing: "border-box",
        backgroundColor: { xs: "white", sm: "#F7F7F7" },
        backgroundImage: { xs: "none", sm: `url(${BackgroundImage})` },
        backgroundSize: "cover",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        position: "relative",
        padding: { xs: "70px 0.5rem 60px 0.5rem", sm: "1rem 1rem 60px 1rem" },
      }}
    >
      {/* Top Left Logo */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 10, sm: 20 },
          left: { xs: 10, sm: 20 },
        }}
      >
        <img
          src={Logo1}
          alt="MBF Logo"
          style={{ height: "auto", width: "auto", maxHeight: "50px" }}
        />
      </Box>

      {/* Top Right Link */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 10, sm: 20 },
          right: { xs: 10, sm: 20 },
        }}
      >
        <Typography
          component="a"
          href={process.env.REACT_APP_ADMIN_SITE_BASE_URL}
          sx={{
            textDecoration: "underline",
            color: "#007BFF",
            fontWeight: 500,
            fontSize: { xs: "14px", sm: "16px" },
          }}
        >
          Go to Admin site
        </Typography>
      </Box>

      {/* Main Capture UI */}
      <PageTransition
        trigger={imageData}
        sx={{
          backgroundColor: "white",
          borderRadius: { xs: "0px", sm: "20px" },
          padding: { xs: "1rem", sm: "24px" },
          width: "100%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 140px)",
          boxShadow: { xs: "none", sm: "4px 4px 20px rgba(0, 123, 255, 0.1)" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" fontWeight={600} color="#212121">
          Set Your Profile Photo
        </Typography>

        {!imageData && hasCameraPermission ? (
          <>
            <Box
              sx={{
                width: { xs: "200px", sm: "100%" },
                height: { xs: "200px", sm: "auto" },
                overflow: "hidden",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "12px",
                }}
              />
            </Box>
            <Button
              onClick={captureImage}
              variant="contained"
              disabled={!hasCameraPermission || !stream} // Disable if no permission or no active stream
              sx={{
                backgroundColor: "#007BFF",
                borderRadius: "10px",
                padding: "10px 20px",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Capture
            </Button>
          </>
        ) : (
          <>
            {imageData ? (
              <>
                <Box
                  component="img"
                  src={imageData}
                  alt="Captured"
                  sx={{
                    width: { xs: "180px", sm: "200px" },
                    height: { xs: "180px", sm: "200px" },
                    objectFit: "cover",
                    borderRadius: "16px",
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    gap: "0.75rem",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    onClick={handleRetake}
                    variant="outlined"
                    sx={{
                      borderRadius: "10px",
                      padding: { xs: "8px 16px", sm: "8px 20px" },
                      textTransform: "none",
                      fontSize: { xs: "13px", sm: "14px" },
                      borderColor: "#007BFF",
                      color: "#007BFF",
                    }}
                  >
                    Take Again
                  </Button>
                  <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={!imageData}
                    sx={{
                      backgroundColor: "#007BFF",
                      borderRadius: "10px",
                      padding: { xs: "8px 16px", sm: "8px 20px" },
                      color: "#fff",
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: { xs: "13px", sm: "14px" },
                    }}
                  >
                    Upload
                  </Button>
                </Box>
              </>
            ) : loader ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "300px",
                }}
              >
                <CircularProgress />
                <Typography mt={2}>Loading camera...</Typography>
              </Box>
            ) : (
              <Typography color="error">
                Camera access denied. Please grant permission to continue.
              </Typography>
            )}
          </>
        )}
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </PageTransition>

      {/* Powered by Section */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.3rem",
          padding: { xs: "8px", sm: "12px" },
          backgroundColor: { xs: "#FFFFFF", sm: "transparent" },
        }}
      >
        <Typography fontSize={{ xs: "11px", sm: "12px" }} color="#666">
          Powered by
        </Typography>
        <img src={mapITlogo} alt="MapIT Logo" style={{ height: "30px" }} />
      </Box>
    </Box>
  );
};

export default Capture;
